
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { ContentStats } from './AdminContentDashboard';
import { groupCategoriesByBroad } from '@/utils/category-mapping';

interface PapersOverviewProps {
  loading: boolean;
  stats?: ContentStats['papers'];
}

const COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe', 
  '#00C49F', '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57'
];

const PapersOverview: React.FC<PapersOverviewProps> = ({ loading, stats }) => {
  // State for source toggles
  const [showAutoImported, setShowAutoImported] = useState(true);
  const [showUserAdded, setShowUserAdded] = useState(true);
  const [showPrivate, setShowPrivate] = useState(true);

  // Prepare data for paper source chart - revised to better represent the data
  const sourceData = stats ? [
    { 
      name: 'External Sources', 
      value: stats.withExternalUrl,
      description: 'Papers fetched from external sources (e.g., arXiv)'
    },
    { 
      name: 'Manual Entries', 
      value: stats.total - stats.withExternalUrl,
      description: 'Papers added manually to the platform'
    }
  ] : [];

  // Prepare data for PDF availability
  const pdfData = stats ? [
    { 
      name: 'With PDF', 
      value: stats.withPdf,
      description: 'Papers with PDF files uploaded'
    },
    { 
      name: 'Without PDF', 
      value: stats.total - stats.withPdf,
      description: 'Papers without PDF files'
    }
  ] : [];

  // Prepare data for categories chart - now grouped by broad categories
  const categoryData = stats?.byCategory ? 
    Object.entries(groupCategoriesByBroad(stats.byCategory))
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value) // Sort by count descending
    : [];

  // Prepare data for monthly trend (kept for backwards compatibility)
  const monthlyData = stats?.byMonth ? 
    Object.entries(stats.byMonth)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, count]) => ({ date, count })) : [];

  // Prepare data for monthly source chart with toggles
  const monthlySourceData = stats?.byMonthBySource 
    ? Object.entries(stats.byMonthBySource)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, counts]) => ({
          date,
          auto_imported: showAutoImported ? counts.auto_imported : 0,
          user_added: showUserAdded ? counts.user_added : 0,
          private: showPrivate ? counts.private : 0,
        }))
    : [];

  // Custom render for pie chart labels to avoid overlapping and cutoff
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Paper Source Distribution - Updated for clarity */}
        <Card className="col-span-1 w-full">
          <CardHeader>
            <CardTitle className="text-lg">Paper Sources</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Pie
                      data={sourceData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={renderCustomizedLabel}
                    >
                      {sourceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name, props) => [value, props.payload.description || name]} />
                    <Legend layout="horizontal" verticalAlign="bottom" align="center" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PDF Availability - New chart to show PDF status */}
        <Card className="col-span-1 w-full">
          <CardHeader>
            <CardTitle className="text-lg">PDF Availability</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Pie
                      data={pdfData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={renderCustomizedLabel}
                    >
                      {pdfData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name, props) => [value, props.payload.description || name]} />
                    <Legend layout="horizontal" verticalAlign="bottom" align="center" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Papers by Category - Now using broad categories */}
        <Card className="col-span-1 w-full">
          <CardHeader>
            <CardTitle className="text-lg">Papers by Category</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={renderCustomizedLabel}
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend layout="horizontal" verticalAlign="bottom" align="center" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Papers Over Time with Visibility Breakdown */}
        <Card className="col-span-1 md:col-span-3 w-full">
          <CardHeader>
            <CardTitle className="text-lg">Papers Published Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[400px] w-full" />
            ) : (
              <>
                {/* Toggle Controls */}
                <div className="flex gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={showAutoImported} 
                      onCheckedChange={(checked) => setShowAutoImported(checked as boolean)} 
                    />
                    <span className="flex items-center gap-1 text-sm">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#8884d8' }} />
                      Auto-imported ({stats?.totalAutoImported || 0})
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={showUserAdded} 
                      onCheckedChange={(checked) => setShowUserAdded(checked as boolean)} 
                    />
                    <span className="flex items-center gap-1 text-sm">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#82ca9d' }} />
                      User-added ({stats?.totalUserAdded || 0})
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={showPrivate} 
                      onCheckedChange={(checked) => setShowPrivate(checked as boolean)} 
                    />
                    <span className="flex items-center gap-1 text-sm">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#ffc658' }} />
                      Private ({stats?.totalPrivate || 0})
                    </span>
                  </label>
                </div>

                {/* Chart */}
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlySourceData} margin={{ top: 5, right: 30, left: 20, bottom: 25 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" angle={-45} textAnchor="end" height={60} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      {showAutoImported && <Bar dataKey="auto_imported" name="Auto-imported" stackId="a" fill="#8884d8" />}
                      {showUserAdded && <Bar dataKey="user_added" name="User-added" stackId="a" fill="#82ca9d" />}
                      {showPrivate && <Bar dataKey="private" name="Private" stackId="a" fill="#ffc658" />}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PapersOverview;
